import logo_gray from "../assets/images/visaro-avatar-gray.png";
import {
  ArrowClockwise,
  ArrowRight,
  Copy,
  CurrencyNgn,
  Eye,
  EyeSlash,
  Gift,
  Lightning,
  Money,
  ShieldCheck,
  Spinner,
  Storefront,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import mobile_preview from "../assets/images/visaro-mobile-app.svg";
import { Link, useNavigate } from "react-router-dom";
import { Fragment, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { formatAsMoney, maskBalance } from "../helpers/numbers";
import useMoment from "../hooks/useMoment";
import { hasUserFlag, setUserFlag } from "../utils/userFlag";
import RecentActivities from "../components/RecentActivities";
import CompleteSetupTaskList, {
  useAccountSetupMetric,
} from "../components/CompleteSetupTaskList";
import { bnplServices } from "../helpers/bnpl-services";
import CreditLimitEligibilityModal from "../components/CreditLimitEligibilityModal";
import FinishModal from "../components/FinishModal";
import useCreditLimitEligibilityStatus from "../hooks/useCreditLimitEligibilityStatus";
import useCreateAlertAccount from "../hooks/useCreateAlertAccount";
import useLoanPreApprovalRequirements from "../hooks/useLoanPreApprovalRequirements";
import useAlertEnquiry from "../hooks/useAlertEnquiry";
import useUserOrgMemberships from "../hooks/useUserOrgMemberships";
import { useMyAdvanceRequests } from "../hooks/useSalaryAdvance";

const SETUP_LIST_DISMISSED = "setup_tasks_dismissed";

const Dashboard = () => {
  const cardRef = useRef<HTMLDivElement>(null);

  const { user, refetch, isRefetching, isFetchingUser } = useAuth();
  const {
    creditLimitEligibilityStatus,
    isLoading: isLoadingCreditLimitEligibility,
  } = useCreditLimitEligibilityStatus();
  const { createAlertAccountAsync } = useCreateAlertAccount();
  const { alertEnquiryData, refetchAlertAccount } = useAlertEnquiry({
    enabled: !!user?.id,
  });
  const { primaryOrg, isError: isOrgError } = useUserOrgMemberships();
  const { data: advanceData } = useMyAdvanceRequests();
  const allAdvances = (advanceData as any)?.data ?? [];
  const pendingAdvanceCount = allAdvances.filter((a: any) =>
    ['pending_hr', 'hr_approved', 'pending_disbursement'].includes(a.status)
  ).length;

  const [creditLimitEligibilityOpen, setCreditLimitEligibilityOpen] =
    useState(false);
  const [finishSetupModalOpen, setFinishSetupModaOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastRefreshed, setLastRefrehsed] = useState<Date | null>(null);

  // The setup list opens by itself once per account, on the first dashboard
  // visit. After the customer closes it, it only opens on request: the
  // "Finish setting up" card, or a feature that needs setup first.
  const autoOpenCheckedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!user?.id) return;
    const id = String(user.id);
    if (autoOpenCheckedFor.current === id) return;
    autoOpenCheckedFor.current = id;
    if (!hasUserFlag(SETUP_LIST_DISMISSED, id)) setFinishSetupModaOpen(true);
  }, [user?.id]);

  const closeFinishSetup = () => {
    setUserFlag(SETUP_LIST_DISMISSED, user?.id);
    setFinishSetupModaOpen(false);
  };

  const moment = useMoment(lastRefreshed);
  const navigate = useNavigate();
  const metric = useAccountSetupMetric();
  const { ready: kycReady } = useLoanPreApprovalRequirements();
  const hasAlertAccount = Boolean(
    user?.account_alert === 1 || alertEnquiryData?.Message?.AccountNumber
  );

  const handleRequestAlertAccount = async () => {
    let creationError: unknown;

    try {
      await createAlertAccountAsync();
    } catch (error) {
      creationError = error;
    }

    const [profileResult, enquiryResult] = await Promise.all([
      refetch().catch(() => null),
      refetchAlertAccount().catch(() => null),
    ]);
    const refreshedUser = profileResult?.data?.data;
    const accountConfirmed = Boolean(
      refreshedUser?.account_alert === 1 ||
        enquiryResult?.data?.Message?.AccountNumber
    );

    if (!accountConfirmed) {
      throw creationError instanceof Error
        ? creationError
        : new Error(
            "We couldn't confirm your wallet activation. Please try again."
          );
    }
  };

  const handleFundWallet = () => {
    navigate("/fund");
  };

  return (
    <div>
      <CompleteSetupTaskList
        isOpen={finishSetupModalOpen}
        handleClose={closeFinishSetup}
      />

      <CreditLimitEligibilityModal
        isOpen={creditLimitEligibilityOpen}
        handleClose={() => setCreditLimitEligibilityOpen(false)}
        eligibilityStatus={creditLimitEligibilityStatus}
        hasAlertAccount={hasAlertAccount}
        kycReady={kycReady}
        onRequestAlertAccount={handleRequestAlertAccount}
      />

      <div className="grid lg:grid-cols-2 gap-5 p-5 lg:p-10 bg-gray-50">
        <div className="flex flex-col gap-5">
          <Fragment>
            <div className="col-span-full bg-white drop-shadow-sm rounded-xl py-5 grid gap-4 overflow-hidden">
              <div className="flex justify-between items-center px-5">
                <div className="text-lg">BNPL Services</div>
              </div>

              <div className="overflow-x-auto custom-scroll-bar py-4 w-full self-stretch flex">
                <div className="flex gap-4 px-5">
                  {bnplServices
                    .sort((a, b) => (a.status === "unavailable" ? 1 : -1))
                    .map((service, key) => (
                      <button
                        key={key}
                        disabled={service.status === "unavailable"}
                        style={{
                          opacity: service.status === "unavailable" ? 0.4 : 1,
                        }}
                        onClick={() =>
                          navigate(
                            service.key === "aviation"
                              ? "/aviation/start"
                              : `/${service.key}`
                          )
                        }
                        className="flex flex-col gap-2 shrink-0"
                      >
                        <img
                          src={service.display_img}
                          alt="logo"
                          draggable={false}
                          className="rounded-2xl h-32 lg:h-36 aspect-video bg-gray-50 border object-cover"
                        />
                        <div className="text-left w-full">
                          {service.bnpl_service_name}{" "}
                          {service.status === "unavailable" && "(soon)"}
                        </div>
                      </button>
                    ))}
                </div>
              </div>

              <div className="flex justify-between items-center px-5">
                <Link
                  to="/services"
                  className="text-blue-600 font-medium opacity-80 hover:opacity-100 transition"
                >
                  Show All
                </Link>
              </div>
            </div>
          </Fragment>
          <Fragment>
            <Link
              to="/marketplace"
              className="col-span-full bg-white drop-shadow-sm p-5 rounded-xl flex items-start md:items-center gap-4"
            >
              <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10">
                <Storefront size={24} />
              </div>

              <div className="flex gap-4 items-center flex-1">
                <div className="flex flex-col">
                  <div className="text-lg text-deepBlue font-medium">
                    Explore Marketplace
                  </div>
                  <div className="text-gray-500">
                    Unlock flexible payments with Buy Now, Pay Later options on
                    the marketplace.
                  </div>
                </div>
              </div>
              <div className="bg-gray-100 p-2 rounded-full">
                <ArrowRight size={18} weight="bold" />
              </div>
            </Link>
            <RecentActivities />
          </Fragment>
        </div>
        <div className="flex flex-col gap-5 -order-1">
          <Fragment>
            <div className="flex flex-col md:flex-row gap-5">
              {/* ─── Available Balance Card ─── */}
              <div className="flex-1 bg-white drop-shadow-sm rounded-xl flex flex-col gap-5 items-start p-5 relative overflow-hidden">
                <img
                  src={logo_gray}
                  alt="Visaro's Logo"
                  className="w-14 aspect-square object-cover"
                />
                <div className="w-full flex-1 flex flex-col gap-2">
                  <div className="text-gray-500 font-medium text-sm">
                    Available Balance
                  </div>
                  {isFetchingUser ? (
                    <>
                      <div className="h-9 w-32 bg-gray-200 rounded animate-pulse" />
                      <div className="h-4 w-48 bg-gray-100 rounded animate-pulse mt-1" />
                      <div className="flex gap-4 mt-3 border-t pt-2 border-gray-50">
                        <div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                            Ledger Balance
                          </div>
                          <div className="h-4 w-24 bg-gray-100 rounded animate-pulse mt-1" />
                        </div>
                      </div>
                      <div className="mt-2 h-4 w-28 bg-gray-100 rounded animate-pulse" />
                    </>
                  ) : (
                    <>
                      <div className="w-full text-3xl font-medium flex items-center justify-between gap-2">
                        <div className="flex flex-col gap-0.5 flex-1">
                          <div className="w-full tracking-wide transition-all duration-300">
                            ₦{" "}
                            {isVisible
                              ? formatAsMoney(
                                  user?.wallet?.available_balance || 0.0
                                )
                              : maskBalance(
                                  user?.wallet?.available_balance || 0.0
                                )}
                          </div>
                          {user?.wallet?.account_number && (
                            <div className="text-xs font-medium text-gray-400 tracking-normal flex items-center gap-1.5">
                              <span>{user?.wallet?.account_number}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const num = user?.wallet?.account_number;
                                  if (num) {
                                    navigator.clipboard.writeText(num);
                                    toast.success("Account number copied!");
                                  }
                                }}
                                className="p-1 rounded text-gray-500 hover:text-visaro-orange hover:bg-gray-100 transition-colors"
                                title="Copy account number"
                              >
                                <Copy size={14} weight="bold" />
                              </button>
                            </div>
                          )}

                          {/* Ledger Balance Section */}
                          <div className="flex gap-4 mt-3 border-t pt-2 border-gray-50">
                            <div>
                              <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                                Ledger Balance
                              </div>
                              <div className="text-sm font-medium text-gray-600 transition-all duration-300">
                                ₦{" "}
                                {isVisible
                                  ? formatAsMoney(
                                      user?.wallet?.ledger_balance || 0.0
                                    )
                                  : maskBalance(
                                      user?.wallet?.ledger_balance || 0.0
                                    )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="w-fit justify-end flex items-center gap-2">
                          <button
                            onClick={() => {
                              refetch().then(() => {
                                setLastRefrehsed(new Date());
                              });
                            }}
                            className="bg-gray-100 rounded-full p-2 text-visaro-orange"
                          >
                            {isRefetching ? (
                              <Spinner
                                className="animate-spin"
                                size={14}
                                weight="bold"
                              />
                            ) : (
                              <ArrowClockwise size={14} weight="bold" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setIsVisible((prev) => !prev);
                            }}
                            className="bg-gray-100 rounded-full p-2 text-visaro-orange"
                          >
                            {isVisible ? (
                              <Eye size={14} weight="bold" />
                            ) : (
                              <EyeSlash size={14} weight="bold" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {lastRefreshed && `Last updated ${moment}`}
                      </div>
                    </>
                  )}
                </div>

              </div>
            </div>
          </Fragment>

          <Fragment>
            {!(metric.all === metric.done) && !metric.isLoading && (
              <FinishModal
                setFinishSetupModaOpen={setFinishSetupModaOpen}
                finishSetupModalOpen={finishSetupModalOpen}
              />
            )}

            {/* Credit Limit Eligibility Card */}
            <div
              onClick={() => {
                if (
                  !isLoadingCreditLimitEligibility &&
                  creditLimitEligibilityStatus?.status !== "pending"
                ) {
                  setCreditLimitEligibilityOpen(true);
                }
              }}
              className={`col-span-full bg-white drop-shadow-sm p-5 rounded-xl flex items-center gap-4 ${
                isLoadingCreditLimitEligibility ||
                creditLimitEligibilityStatus?.status === "pending"
                  ? "cursor-default"
                  : "cursor-pointer"
              }`}
            >
              <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10">
                <CurrencyNgn size={24} />
              </div>

              <div className="flex gap-4 items-center flex-1">
                {isLoadingCreditLimitEligibility ? (
                  <div className="flex flex-col gap-2 flex-1">
                    <div className="h-5 bg-gray-200 rounded w-48 animate-pulse" />
                    <div className="h-4 bg-gray-100 rounded w-64 animate-pulse" />
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="text-lg text-deepBlue font-medium">
                      {creditLimitEligibilityStatus?.status === "pending"
                        ? "Eligibility Under Review"
                        : creditLimitEligibilityStatus?.status === "approved" ||
                          creditLimitEligibilityStatus?.status === "declined"
                        ? "Review Completed"
                        : "Check Credit Limit Eligibility"}
                    </div>
                    <div className="text-gray-500">
                      {creditLimitEligibilityStatus?.status === "pending"
                        ? "Your eligibility check is being reviewed"
                        : creditLimitEligibilityStatus?.status === "approved" ||
                          creditLimitEligibilityStatus?.status === "declined"
                        ? "Click to view your eligibility results"
                        : "Check the credit limit Visaro may be able to offer you"}
                    </div>
                  </div>
                )}
              </div>
              {!isLoadingCreditLimitEligibility &&
                creditLimitEligibilityStatus?.status !== "pending" && (
                  <div className="bg-gray-100 p-2 rounded-full">
                    <ArrowRight size={18} weight="bold" />
                  </div>
                )}
            </div>

            {/* Loan Pre-Approval Card */}
            <button
              type="button"
              onClick={() => navigate("/loans/request")}
              className="col-span-full w-full bg-white drop-shadow-sm p-5 rounded-xl flex items-center gap-4 text-left"
            >
              <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10 shrink-0">
                <ShieldCheck size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-lg text-deepBlue font-medium">
                  Loan Pre-Approval
                </div>
                <div className="text-gray-500">
                  Request a general-purpose loan for partner review
                </div>
              </div>
              <div className="bg-gray-100 p-2 rounded-full shrink-0">
                <ArrowRight size={18} weight="bold" />
              </div>
            </button>

            {/* Salary Advance — visible to org members; also shown on core-service error (backend guards submit) */}
            {(primaryOrg || isOrgError) && (
              <div className="col-span-full bg-white drop-shadow-sm rounded-xl overflow-hidden">
                <button
                  onClick={() => navigate("/salary-advance")}
                  className="w-full p-5 flex items-center gap-4 text-left"
                >
                  <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10 shrink-0">
                    <Money size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-lg text-deepBlue font-medium">
                        Request Salary Advance
                      </span>
                      {pendingAdvanceCount > 0 && (
                        <span className="bg-visaro-orange text-white text-xs font-bold px-2 py-0.5 rounded-full">
                          {pendingAdvanceCount} pending
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500 text-sm mt-0.5">
                      Get an advance on your salary — repay in installments
                    </div>
                  </div>
                  <div className="bg-gray-100 p-2 rounded-full shrink-0">
                    <ArrowRight size={18} weight="bold" />
                  </div>
                </button>
                {allAdvances.length > 0 && (
                  <button
                    onClick={() => navigate("/salary-advance/status")}
                    className="w-full px-5 py-2.5 border-t border-gray-100 text-sm text-deepBlue font-medium text-left hover:bg-gray-50 transition flex items-center justify-between"
                  >
                    <span>View my requests ({allAdvances.length})</span>
                    <ArrowRight size={14} weight="bold" />
                  </button>
                )}
              </div>
            )}

            {/* Fund Wallet */}
            <button
              onClick={handleFundWallet}
              className="col-span-full bg-white drop-shadow-sm p-5 rounded-xl flex items-center gap-4 text-left w-full"
            >
              <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10">
                <Money size={24} />
              </div>

              <div className="flex gap-4 items-center flex-1">
                <div className="flex flex-col">
                  <div className="text-lg text-deepBlue font-medium">
                    Fund your Wallet
                  </div>
                  <div className="text-gray-500">Add money to your wallet</div>
                </div>
              </div>
              <div className="bg-gray-100 p-2 rounded-full">
                <ArrowRight size={18} weight="bold" />
              </div>
            </button>

            {/* Pay Bills */}
            <Link
              to="/payments/bills"
              className="col-span-full bg-white drop-shadow-sm p-5 rounded-xl flex items-start md:items-center gap-4"
            >
              <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10">
                <Lightning size={24} />
              </div>
              <div className="flex-1">
                <div className="font-medium text-lg">Pay Bills</div>
                <div className="text-gray-500">
                  Pay your airtime, data, and electricity bills easily
                </div>
              </div>
              <div className="p-2 bg-gray-100 rounded-full hover:scale-105 transition-all cursor-pointer">
                <ArrowRight weight="bold" size={18} />
              </div>
            </Link>

            {/* Invite friends */}
            <Link
              to="/referrals"
              className="col-span-full bg-white drop-shadow-sm p-5 rounded-xl flex items-start md:items-center gap-4"
            >
              <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10">
                <Gift size={24} />
              </div>
              <div className="flex-1">
                <div className="font-medium text-lg">Invite friends</div>
                <div className="text-gray-500">
                  Share your code or link. Rewards apply when Visaro referral
                  rules are met.
                </div>
              </div>
              <div className="p-2 bg-gray-100 rounded-full hover:scale-105 transition-all cursor-pointer">
                <ArrowRight weight="bold" size={18} />
              </div>
            </Link>

            {/* Get Visaro App */}
            <div
              ref={cardRef}
              className="col-span-1 bg-white drop-shadow-sm p-5 rounded-xl flex flex-col gap-5"
            >
              <div className="flex justify-between items-center">
                <div>Get the Visaro App</div>
                <X weight="bold" onClick={() => cardRef.current?.remove()} />
              </div>
              <div className="flex items-center justify-center">
                <img src={mobile_preview} alt="mobile-app" />
              </div>
              <div className="flex justify-between items-center">
                <a
                  href="https://www.apple.com/app-store/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 font-medium opacity-80 hover:opacity-100 transition"
                >
                  Download the Visaro App
                </a>
              </div>
            </div>
          </Fragment>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
